import React, { useState, useEffect, useContext, useCallback, useRef } from "react";
import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { TabView, TabPanel } from 'primereact/tabview';
import { useWeb3React } from "@web3-react/core";
import Web3 from "web3";
import TokenSelectorModal from './TokenSelectorModal';
import TokenInput from './TokenInput';
import ContractContext from '../../ContractContext';
import ierc20Abi from '../../contracts/ierc20Abi';
import lockerAbi from "../../contracts/lockerAbi";
import { formatBalanceWithIcon, formatDecimals, getTokenLogo, formatCurrencyExtended, formatBalanceWithLogo, formatWalletAddress } from '../../utils';
import lockerbay_logo from "../../imgs/lockerbay_logo.png";
import { infoOutlineStyle, miniTokenCardStyle } from './TileStyles';
import { Zoom } from "@mui/material";
import BigNumber from "bignumber.js";
import { Toast } from "primereact/toast";
import { Card as NextCard } from "@nextui-org/react";
import { RadioButton } from 'primereact/radiobutton';
import { Inplace, InplaceDisplay, InplaceContent } from 'primereact/inplace';
import RewardsPending from '../subcomponents_radar/RewardsPending';

const containerStyle = {
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
    padding: '0 10px',
};

const subTileStyle = {
    background: '#1E2732',
    border: '1px solid #eee',
    borderRadius: '8px',
    padding: '10px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    fontSize: '14px',
    color: '#eee',
};

const carouselItemStyle = {
    minWidth: '200px',
    scrollSnapAlign: 'center',
    flexShrink: 0,
    transition: 'transform 0.3s ease', // Smooth transition for item scaling
};



const LockerBay = React.memo(({ tokenSelections, refreshData, imageReferences }) => {
    const { account, library } = useWeb3React();
    const contractContext = useContext(ContractContext);
    const contracts = contractContext.contracts;
    const [timedLockToken, setTimedLockToken] = useState(null);
    const [personalLockToken, setPersonalLockToken] = useState(null);
    const [timedLockBalance, setTimedLockBalance] = useState(0);
    const [personalLockBalance, setPersonalLockBalance] = useState(0);
    const [timedLockAmount, setTimedLockAmount] = useState('');
    const [personalLockAmount, setPersonalLockAmount] = useState('');
    const [unlockTime, setUnlockTime] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [filteredTokens, setFilteredTokens] = useState([]);
    const [personalLocker, setPersonalLocker] = useState(null);
    const [timedLocks, setTimedLocks] = useState([]);
    const [personalLockTokens, setPersonalLockTokens] = useState([]);
    const [personalLockBalances, setPersonalLockBalances] = useState([]);
    const [allowanceTimedLock, setAllowanceTimedLock] = useState(new BigNumber(0));
    const [allowancePersonalLock, setAllowancePersonalLock] = useState(new BigNumber(0));
    const [unlockMode, setUnlockMode] = useState(false);
    const [existingLockTime, setExistingLockTime] = useState(null);
    const [isLockTimeValid, setIsLockTimeValid] = useState(true);
    const [lockOption, setLockOption] = useState('lockOnly');
    const toast = useRef(null);

    const getTokenLogoUrl = (token) => getTokenLogo(imageReferences, token);

    useEffect(() => {
        if (!tokenSelections) return;
        const storedTimedLockToken = localStorage.getItem('timedLockToken');
        const storedPersonalLockToken = localStorage.getItem('personalLockToken');

        const foundTimedLockToken = tokenSelections.find(token => token.address === storedTimedLockToken);
        const foundPersonalLockToken = tokenSelections.find(token => token.address === storedPersonalLockToken);

        if (foundTimedLockToken) {
            setTimedLockToken(foundTimedLockToken);
        } else {
            const defaultToken = tokenSelections.find(token => token.address === '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c');
            setTimedLockToken(defaultToken);
        }

        if (foundPersonalLockToken) {
            setPersonalLockToken(foundPersonalLockToken);
        } else {
            const defaultToken = tokenSelections.find(token => token.address === '0xbb4CdB9CBd36B01bD1cBaEBF2De08d9173bc095c');
            setPersonalLockToken(defaultToken);
        }
    }, [tokenSelections]);

    const getTokenBalance = useCallback(async (token) => {
        try {
            if (!library?.provider || !token) return 0;
            const web3 = new Web3(library.provider);
            if (token.label === 'WBNB') {
                const balance = await web3.eth.getBalance(account);
                return balance;
            } else {
                const contract = new web3.eth.Contract(ierc20Abi, token.address);
                const balance = await contract.methods.balanceOf(account).call();
                return balance;
            }
        } catch (error) {
            console.error("Error fetching token balance:", error);
            return 0;
        }
    }, [account, library]);

    useEffect(() => {
        if (timedLockToken && timedLockToken.address) {
            getTokenBalance(timedLockToken).then(balance => setTimedLockBalance(balance));
        } else {
            setTimedLockBalance(0);
        }
    }, [timedLockToken, account, library]);

    useEffect(() => {
        if (personalLockToken && personalLockToken.address) {
            getTokenBalance(personalLockToken).then(balance => setPersonalLockBalance(balance));
        } else {
            setPersonalLockBalance(0);
        }
    }, [personalLockToken, account, library]);

    const fetchPersonalLocker = useCallback(async () => {
        try {
            const lockerAddress = await contracts.lockerBay.methods.getUserPersonalLocker(account).call();
            if (lockerAddress !== "0x0000000000000000000000000000000000000000") {
                setPersonalLocker(lockerAddress);
                const web3 = new Web3(library.provider);
                const lockerContract = new web3.eth.Contract(lockerAbi, lockerAddress);
                const balances = await lockerContract?.methods.getAllTokensAndBalances().call();
                setPersonalLockTokens(balances[0]);
                setPersonalLockBalances(balances[1]);
            }
        } catch (error) {
            console.error("Error fetching personal locker:", error);
        }
    }, [account, tokenSelections, contracts.lockerBay, library]);

    const fetchTimedLocks = useCallback(async () => {
        try {
            const data = await contracts?.lockerBay.methods.getUserLockerBayData(account).call();
            setTimedLocks(data);
        } catch (error) {
            console.error("Error fetching timed locks:", error);
        }
    }, [account, tokenSelections, contracts.lockerBay]);

    const fetchAllowance = useCallback(async (contractAddress, tokenAddress) => {
        const web3 = new Web3(library.provider);
        const tokenContract = new web3.eth.Contract(ierc20Abi, tokenAddress);
        const allowance = await tokenContract.methods.allowance(account, contractAddress).call();
        return new BigNumber(allowance);
    }, [account, library]);

    useEffect(() => {
        if (account && timedLockToken && timedLockToken.address) {
            fetchAllowance(contracts.lockerBay?._address, timedLockToken?.address).then(setAllowanceTimedLock);
        }
        if (account && personalLockToken && personalLockToken?.address && personalLocker) {
            fetchAllowance(personalLocker, personalLockToken?.address).then(setAllowancePersonalLock);
        }
    }, [account, contracts.lockerBay, timedLockToken, personalLockToken, personalLocker, fetchAllowance]);

    useEffect(() => {
        if (account) {
            fetchPersonalLocker();
            fetchTimedLocks();
        }
    }, [account, fetchPersonalLocker, fetchTimedLocks]);

    useEffect(() => {
        if (timedLockToken && timedLocks[0]?.length > 0) {
            const existingLock = timedLocks[0].find((token, index) => token.toLowerCase() === timedLockToken.address.toLowerCase());
            if (existingLock) {
                const existingLockIndex = timedLocks[0].indexOf(existingLock);
                setExistingLockTime(timedLocks[2][existingLockIndex]);
            } else {
                setExistingLockTime(null);
            }
        } else {
            setExistingLockTime(null);
        }
    }, [timedLockToken, timedLocks]);

    useEffect(() => {
        if (existingLockTime && unlockTime) {
            setIsLockTimeValid(new Date(unlockTime).getTime() >= existingLockTime * 1000);
        } else {
            setIsLockTimeValid(true);
        }
    }, [existingLockTime, unlockTime]);

    const handleTokenClick = useCallback((type) => {
        setShowModal(type);
        setFilteredTokens(tokenSelections?.filter(t => t.address.toLowerCase() !== (type === 'timed' ? timedLockToken?.address.toLowerCase() : personalLockToken?.address.toLowerCase())));
    }, [timedLockToken, personalLockToken, tokenSelections]);

    const handleTokenSelect = useCallback((token, type) => {
        if (type === 'timed') {
            setTimedLockToken(token);
            setTimedLockAmount(''); // Reset the amount to empty
            localStorage.setItem('timedLockToken', token.address);
        } else {
            setPersonalLockToken(token);
            setPersonalLockAmount(''); // Reset the amount to empty
            localStorage.setItem('personalLockToken', token.address);
        }
        setShowModal(false);
    }, []);

    const handleCloseModal = useCallback(() => {
        setShowModal(false);
    }, []);

    const handleApprove = async (contractAddress, amount, isPersonalLocker = false) => {
        const web3 = new Web3(library.provider);
        const tokenContract = new web3.eth.Contract(ierc20Abi, isPersonalLocker ? personalLockToken.address : timedLockToken.address);
        const tx = tokenContract.methods.approve(contractAddress, amount.toFixed());
        const gasEstimate = await tx.estimateGas({ from: account });
        const transactionPromise = tx.send({
            from: account,
            gas: gasEstimate
        });

        transactionPromise.on('transactionHash', hash => {
            toast.current.show({ severity: 'info', summary: 'Approval Submitted', detail: `Approval transaction has been submitted. Transaction Hash: ${hash}`, life: 3000 });
        });

        await transactionPromise;
        toast.current.show({ severity: 'success', summary: 'Approval Success', detail: 'Approval transaction has been successfully completed.', life: 3000 });

        const newAllowance = await fetchAllowance(contractAddress, isPersonalLocker ? personalLockToken.address : timedLockToken.address);
        if (isPersonalLocker) {
            setAllowancePersonalLock(newAllowance);
        } else {
            setAllowanceTimedLock(newAllowance);
        }
        refreshData();
    };

    const handleLockTokens = async () => {
        if (!timedLockToken || !timedLockAmount || !unlockTime) return;
        const unlockTimestamp = Math.floor(new Date(unlockTime).getTime() / 1000);
        const amountWithDecimals = new BigNumber(timedLockAmount).toFixed();
        console.log("Locking", amountWithDecimals, timedLockToken.label, "until", unlockTimestamp);
        console.log("Formatted time", new Date(unlockTimestamp * 1000).toLocaleString());
        try {
            await contracts.lockerBay.methods.lockTokensWithTimer(timedLockToken.address, amountWithDecimals, unlockTimestamp).send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Tokens Locked', detail: `Locked ${timedLockAmount} ${timedLockToken.label} until ${new Date(unlockTimestamp * 1000).toLocaleString()}`, life: 3000 });
            setTimedLockAmount('');
        } catch (error) {
            console.error("Error locking tokens:", error);
            toast.current.show({ severity: 'error', summary: 'Locking Error', detail: error.toString(), life: 5000 });
        }
        refreshData();
    };

    const handleLockAdditionalTokens = async () => {
        if (!timedLockToken || !timedLockAmount) return;
        const amountWithDecimals = new BigNumber(timedLockAmount).toFixed();

        try {
            await contracts.lockerBay.methods.lockAdditionalTokens(timedLockToken.address, amountWithDecimals).send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Additional Tokens Locked', detail: `Locked additional ${timedLockAmount} ${timedLockToken.label}`, life: 3000 });
        } catch (error) {
            console.error("Error locking additional tokens:", error);
            toast.current.show({ severity: 'error', summary: 'Locking Error', detail: error.toString(), life: 5000 });
        }
        refreshData();
    };

    const handleExtendLockerTime = async () => {
        if (!timedLockToken || !unlockTime) return;
        const unlockTimestamp = Math.floor(new Date(unlockTime).getTime() / 1000);

        try {
            await contracts.lockerBay.methods.extendLockerTime(timedLockToken.address, unlockTimestamp).send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Locker Time Extended', detail: `Extended lock time for ${timedLockToken.label} to ${unlockTime}`, life: 3000 });
        } catch (error) {
            console.error("Error extending lock time:", error);
            toast.current.show({ severity: 'error', summary: 'Extension Error', detail: error.toString(), life: 5000 });
        }
        refreshData();
    };

    //Alternative if not using extending vs setting a new time
    // const handleExtend7Days_Upgraded = async (tokenAddress) => {
    //     const newUnlockTimestamp = calculateNewUnlockTime();
    //     try {
    //         await contracts.lockerBay.methods.extendLockerTime(tokenAddress, newUnlockTimestamp).send({ from: account });
    //         toast.current.show({ severity: 'success', summary: 'Locker Time Extended', detail: `Extended lock time by 7 days for ${tokenAddress}`, life: 3000 });
    //     } catch (error) {
    //         console.error("Error extending lock time:", error);
    //         toast.current.show({ severity: 'error', summary: 'Extension Error', detail: error.toString(), life: 5000 });
    //     }
    //     refreshData();
    // };

    const handleExtend7Days = async (tokenAddress, existingUnlockTime) => {
        const extensionAmount = calculateExtensionAmount(existingUnlockTime);
        console.log("Extending lock time by 7 minutes for", tokenAddress);
        console.log("Existing unlock time:", existingUnlockTime);
        console.log("Extension amount:", extensionAmount);
        console.log("New unlock time:", parseFloat(existingUnlockTime) + parseFloat(extensionAmount));
        console.log("Formatted new unlock time:", new Date((parseFloat(existingUnlockTime) + parseFloat(extensionAmount)) * 1000).toLocaleString());
        try {
            await contracts.lockerBay.methods.extendLockerTime(tokenAddress, extensionAmount).send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Locker Time Extended', detail: `Extended lock time by 7 minutes for ${tokenAddress}`, life: 3000 });
        } catch (error) {
            console.error("Error extending lock time:", error);
            toast.current.show({ severity: 'error', summary: 'Extension Error', detail: error.toString(), life: 5000 });
        }
        refreshData();
    };

    const handleUnlockTokens = async (token, balance) => {
        if (!token || !balance) return;
        const amountWithDecimals = new BigNumber(balance).toFixed();
        console.log("Unlocking", amountWithDecimals, token.label);
        try {
            await contracts.lockerBay.methods.unlockTokens(token.address, amountWithDecimals).send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Tokens Unlocked', detail: `Unlocked ${balance} ${token.label}`, life: 3000 });
        } catch (error) {
            console.error("Error unlocking tokens:", error);
            toast.current.show({ severity: 'error', summary: 'Unlocking Error', detail: error.toString(), life: 5000 });
        }
        refreshData();
    };


    const handleCreatePersonalLocker = async () => {
        try {
            await contracts.lockerBay.methods.createPersonalLocker().send({ from: account });
            fetchPersonalLocker();
            toast.current.show({ severity: 'success', summary: 'Personal Locker Created', detail: 'Your personal locker has been created.', life: 3000 });
        } catch (error) {
            console.error("Error creating personal locker:", error);
            toast.current.show({ severity: 'error', summary: 'Creation Error', detail: error.toString(), life: 5000 });
        }
        refreshData();
    };

    const handlePersonalLockTokens = async () => {
        if (!personalLockToken || !personalLockAmount) return;
        const amountWithDecimals = new BigNumber(personalLockAmount).toFixed();
        console.log("Locking", amountWithDecimals, personalLockToken.label, "in personal locker");
        try {
            const web3 = new Web3(library.provider);
            const lockerContract = new web3.eth.Contract(lockerAbi, personalLocker);
            await lockerContract.methods.lockTokens(personalLockToken.address, amountWithDecimals).send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Tokens Locked in Personal Locker', detail: `Locked ${personalLockAmount} ${personalLockToken.label} in personal locker`, life: 3000 });
            setPersonalLockAmount('');
        } catch (error) {
            console.error("Error locking tokens in personal locker:", error);
            toast.current.show({ severity: 'error', summary: 'Locking Error', detail: error.toString(), life: 5000 });
        }
        refreshData();
    };

    const handlePersonalUnlockTokens = async (token, unlockAmount) => {
        if (!token || !unlockAmount) return;
        //const amountWithDecimals = new BigNumber(unlockAmount).multipliedBy(new BigNumber(10).pow(token.decimals)).toFixed();
        console.log("Unlocking", unlockAmount, token.label, "from personal locker");
        try {
            const web3 = new Web3(library.provider);
            const lockerContract = new web3.eth.Contract(lockerAbi, personalLocker);
            await lockerContract.methods.unlockTokens(token.address, unlockAmount).send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Tokens UnlockedamountWithDecimals from Personal Locker', detail: `Unlocked ${unlockAmount} ${token.label} from personal locker`, life: 3000 });
        } catch (error) {
            console.error("Error unlocking tokens from personal locker:", error);
            toast.current.show({ severity: 'error', summary: 'Unlocking Error', detail: error.toString(), life: 5000 });
        }
        refreshData();
    };

    const handleToggleUnlockMode = () => {
        setUnlockMode(!unlockMode);
        if (unlockMode) {
            setRadarView(false);
        }
    };

    const handleRadarView = () => {
        setRadarView(!radarView);
    };


    const calculateExtensionAmount = (existingUnlockTime) => {
        const currentDate = new Date();
        const newUnlockTime = new Date(currentDate.getTime() + 7 * 60 * 1000); // 7 minutes from now
        const extensionAmount = Math.floor((newUnlockTime.getTime() - existingUnlockTime * 1000) / 1000); // in seconds
        return extensionAmount > 0 ? extensionAmount : 0;
    };





    const [unclaimedRewards, setUnclaimedRewards] = useState([]);

    // Fetch unclaimed rewards for each token
    const fetchUnclaimedRewards = useCallback(async () => {
        if (!personalLocker || !tokenSelections.length) return;
        try {
            const web3 = new Web3(library.provider);
            const lockerContract = new web3.eth.Contract(lockerAbi, personalLocker);
            const rewards = await Promise.all(
                tokenSelections.map(async (token) => {
                    const reward = await lockerContract.methods.getUnclaimedRewards(token.address).call();
                    return { token, reward: new BigNumber(reward) };
                })
            );
            setUnclaimedRewards(rewards.filter(r => r.reward.gt(0)));
        } catch (error) {
            console.error("Error fetching unclaimed rewards:", error);
        }
    }, [personalLocker, tokenSelections, library]);

    useEffect(() => {
        fetchUnclaimedRewards();
    }, [fetchUnclaimedRewards]);

    const handleClaimRewardsToWallet = async (token) => {
        try {
            const web3 = new Web3(library.provider);
            const lockerContract = new web3.eth.Contract(lockerAbi, personalLocker);
            await lockerContract.methods.claimRewardsToWallet(token.address).send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Rewards Claimed', detail: `Successfully claimed rewards for ${token.label}`, life: 3000 });
            fetchUnclaimedRewards(); // Refresh rewards after claiming
        } catch (error) {
            console.error("Error claiming rewards:", error);
            toast.current.show({ severity: 'error', summary: 'Claiming Error', detail: error.toString(), life: 5000 });
        }
    };

    const handleClaimRewardsToLocker = async (token) => {
        try {
            const web3 = new Web3(library.provider);
            const lockerContract = new web3.eth.Contract(lockerAbi, personalLocker);
            await lockerContract.methods.transferRewardsToLocker(token.address).send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Rewards Claimed', detail: `Successfully claimed rewards for ${token.label}`, life: 3000 });
            fetchUnclaimedRewards(); // Refresh rewards after claiming
        } catch (error) {
            console.error("Error claiming rewards:", error);
            toast.current.show({ severity: 'error', summary: 'Claiming Error', detail: error.toString(), life: 5000 });
        }
    };


    const [secondaryOwner, setSecondaryOwner] = useState(null);
    const [newSecondaryOwner, setNewSecondaryOwner] = useState('');
    const [isPrimaryOwnerLocked, setIsPrimaryOwnerLocked] = useState(false);

    useEffect(() => {
        const fetchSecondaryOwner = async () => {
            if (personalLocker) {
                const web3 = new Web3(library.provider);
                const lockerContract = new web3.eth.Contract(lockerAbi, personalLocker);
                const secondaryOwner = await lockerContract.methods.secondaryOwner().call();
                setSecondaryOwner(secondaryOwner);
            }
        };

        const fetchPrimaryOwnerLockStatus = async () => {
            if (personalLocker) {
                const web3 = new Web3(library.provider);
                const lockerContract = new web3.eth.Contract(lockerAbi, personalLocker);
                const isLocked = await lockerContract.methods.lockPrimaryOwner().call();
                setIsPrimaryOwnerLocked(isLocked);
            }
        };

        fetchSecondaryOwner();
        fetchPrimaryOwnerLockStatus();
    }, [personalLocker, library]);

    const handleSetSecondaryOwner = async (address) => {
        try {
            const web3 = new Web3(library.provider);
            const lockerContract = new web3.eth.Contract(lockerAbi, personalLocker);
            await lockerContract.methods.setSecondaryOwner(address).send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Secondary Owner Set', detail: `Secondary owner set to ${address}`, life: 3000 });
            setSecondaryOwner(address);
        } catch (error) {
            console.error("Error setting secondary owner:", error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.toString(), life: 5000 });
        }
    };

    const handleLockPrimaryOwnerWithdraws = async () => {
        try {
            const web3 = new Web3(library.provider);
            const lockerContract = new web3.eth.Contract(lockerAbi, personalLocker);
            await lockerContract.methods.lockPrimaryOwnerWithdraws().send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Primary Owner Locked', detail: 'Primary owner withdrawals locked', life: 3000 });
            setIsPrimaryOwnerLocked(true);
        } catch (error) {
            console.error("Error locking primary owner withdrawals:", error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.toString(), life: 5000 });
        }
    };

    const handleUnlockAllTokens = async () => {
        try {
            const web3 = new Web3(library.provider);
            const lockerContract = new web3.eth.Contract(lockerAbi, personalLocker);
            await lockerContract.methods.unlockAllTokens().send({ from: account });
            toast.current.show({ severity: 'success', summary: 'Tokens Unlocked', detail: 'All tokens have been unlocked', life: 3000 });
            refreshData();
        } catch (error) {
            console.error("Error unlocking all tokens:", error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: error.toString(), life: 5000 });
        }
    };


    const [radarView, setRadarView] = useState(false);

    return (
        <div style={containerStyle}>
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                ...infoOutlineStyle,
                paddingTop: '0px',
                paddingBottom: '10px',
                //paddingLeft: '5px',
                //paddingRight: '5px',
            }}>
                <img src={lockerbay_logo} alt="lockerbay logo" style={{ maxWidth: '250px', maxHeight: '50px', marginBottom: '15px', marginTop: '25px', paddingTop: '5px' }} />
            </div>

            <TabView>
                <TabPanel header="Timed Locks">
                    <h3>Timed Lockers</h3>

                    <div
                        style={{
                            borderRadius: '25px',
                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            paddingBottom: '5px',
                            marginBottom: '20px',
                        }}

                    >
                        <TokenInput
                            onSelectToken={() => handleTokenClick('timed')}
                            token={timedLockToken}
                            balance={timedLockBalance}
                            onAmountChange={setTimedLockAmount}
                            amountIn={timedLockAmount}
                            getTokenLogoUrl={getTokenLogoUrl}
                        />
                        {(existingLockTime &&
                            existingLockTime > new Date().getTime() / 1000) &&
                            (
                                <div style={{
                                    marginBottom: '20px',
                                    justifyContent: 'center', alignItems: 'center', display: 'flex', gap: '10px',
                                }}>
                                    <RadioButton inputId="lockOnly" name="lockOption" value="lockOnly" onChange={(e) => setLockOption(e.value)} checked={lockOption === 'lockOnly'} />
                                    <label htmlFor="lockOnly" style={{ marginRight: '10px' }}>Add to Lock Only</label>
                                    <RadioButton inputId="extend" name="lockOption" value="extend" onChange={(e) => setLockOption(e.value)} checked={lockOption === 'extend'} />
                                    <label htmlFor="extend">Extend Date & Add to Lock</label>
                                </div>
                            )}
                        {(existingLockTime && lockOption === 'extend') || !existingLockTime || existingLockTime < new Date().getTime() / 1000 ? (
                            <div style={{
                                marginBottom: '20px',
                                justifyContent: 'center', alignItems: 'center', display: 'flex',
                                alignContent: 'center',
                                textAlign: 'right',
                                fontSize: '14px',
                                padding: '5px',
                            }}>
                                {existingLockTime && existingLockTime > new Date().getTime() / 1000 ? 'New' : 'Enter'} Unlock Date/Time:
                                <div style={{
                                    marginLeft: '5px',
                                    marginRight: '5px',
                                }}>
                                    <Calendar
                                        value={unlockTime}
                                        onChange={(e) => setUnlockTime(e.value)}
                                        showTime
                                        showSeconds
                                        placeholder="Select a Date & Time"
                                        minDate={new Date()}
                                        hourFormat="12"
                                        showIcon
                                        showButtonBar
                                    />
                                </div>
                            </div>
                        ) : null}

                    </div>
                    <div style={{
                        marginBottom: '20px',
                        justifyContent: 'center', alignItems: 'center', display: 'flex', gap: '5px',
                    }}>
                        <Button
                            label="Approve"
                            onClick={() => handleApprove(contracts.lockerBay._address, new BigNumber(timedLockAmount), false)}
                            disabled={!timedLockAmount || timedLockAmount === '0' || allowanceTimedLock.gte(new BigNumber(timedLockAmount))}
                            style={{ marginBottom: '10px', width: '100%', borderRadius: '25px' }}
                            size="small"
                        />
                        <Button
                            label="Lock Tokens"
                            onClick={handleLockTokens}
                            disabled={!timedLockAmount || timedLockAmount === '0' || allowanceTimedLock.lt(new BigNumber(timedLockAmount))}
                            style={{ marginBottom: '10px', width: '100%', borderRadius: '25px' }}
                            size="small"
                        />
                    </div>




                    {timedLocks && timedLocks[0]?.length > 0 && (
                        <div >

                            <div style={{ display: 'flex', alignContent: 'space-between', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                                <div style={{
                                    fontSize: '14px', fontWeight: 'normal',
                                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                    borderRadius: '15px',
                                    padding: '10px',
                                }}>
                                    Your Total Value Locked: <br />
                                    <div style={{
                                        fontSize: '18px', fontWeight: 'bold',
                                    }}>
                                        {formatCurrencyExtended(
                                            timedLocks[1]?.reduce((acc, balance, index) => {
                                                const token = tokenSelections?.find(t => t?.address.toLowerCase() === timedLocks[0][index]?.toLowerCase());
                                                return acc + (parseFloat(balance) * parseFloat(token?.price) / (10 ** token?.decimals));
                                            }, 0))
                                        }
                                    </div>
                                </div>

                                <div style={{
                                    fontSize: '14px', fontWeight: 'normal',
                                    backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                    borderRadius: '15px',
                                    padding: '10px',
                                    overflow: 'auto',
                                }}>
                                    LockerBay Address - Projects Exclude from Fees: <br />
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        fontSize: '18px', fontWeight: 'bold',
                                    }}>
                                        {formatWalletAddress(contracts.lockerBay?._address)}
                                        <Button icon="pi pi-copy" className="p-button-text" onClick={() => navigator.clipboard.writeText(contracts.lockerBay?._address)} />
                                    </div>
                                </div>
                            </div>

                            <h3>Your Timed Locks</h3>


                            {timedLocks[0].map((token, index) => {
                                const _token = tokenSelections?.find(t => t?.address.toLowerCase() === token.toLowerCase());
                                const balance = timedLocks[1][index];
                                const unlockTimestamp = timedLocks[2][index];
                                const expired = new Date().getTime() > unlockTimestamp * 1000;

                                if (parseFloat(balance) > 0) {
                                    return (

                                        <NextCard key={token} style={miniTokenCardStyle(getTokenLogoUrl(_token))}>
                                            <NextCard.Body style={{ padding: '3px', overflow: 'auto' }}>
                                                <div style={{ display: 'flex', alignContent: 'space-between', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold', fontSize: '16px', flexWrap: 'wrap' }}>
                                                        <div style={{ flex: '1 1 auto' }}>{formatBalanceWithLogo(balance, _token, imageReferences)}</div>
                                                        <div style={{ flex: '0 1 auto' }}>{_token?.label}</div>
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap', textAlign: 'right' }}>
                                                        <div style={{ flex: '0 1 auto' }}>
                                                            {expired ? (
                                                                <div
                                                                    style={{
                                                                        gap: '3px',
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'flex-end',
                                                                        flexWrap: 'wrap',
                                                                    }}
                                                                >
                                                                    <Button icon="pi pi-lock" label="7m Relock" rounded size='small' onClick={() => handleExtend7Days(_token.address, unlockTimestamp)} />
                                                                    <Button icon="pi pi-lock-open" label="Unlock" rounded size='small' onClick={() => handleUnlockTokens(_token, balance)} />
                                                                </div>
                                                            ) : (
                                                                <div style={{
                                                                    fontSize: '14px',
                                                                    borderRadius: '25px',
                                                                    backgroundColor: 'rgba(0, 0, 0, 0.25)',
                                                                    padding: '5px',
                                                                }}>
                                                                    <i className="pi pi-lock"></i> {new Date(unlockTimestamp * 1000).toLocaleString()}
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div style={{ fontSize: '16px', fontWeight: 'normal', flex: '1 1 auto' }}>
                                                            {formatCurrencyExtended(parseFloat(balance) * parseFloat(_token?.price) / (10 ** _token?.decimals))}
                                                        </div>
                                                    </div>
                                                </div>
                                            </NextCard.Body>
                                        </NextCard>

                                    );
                                }
                                return null;
                            })}

                        </div>
                    )}


                </TabPanel>
                <TabPanel header="Personal Locker">
                    {personalLocker ? (
                        <div>

                            <Inplace>
                                <InplaceDisplay>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        fontSize: '18px', fontWeight: 'bold',
                                    }}>
                                        <Button label="Add To Locker" icon="pi pi-plus"
                                            rounded size='small'
                                            style={{
                                                alignSelf: 'center',
                                            }}
                                        />
                                    </div>
                                </InplaceDisplay>
                                <InplaceContent>
                                    <h3>Add To Locker</h3>
                                    <TokenInput
                                        onSelectToken={() => handleTokenClick('personal')}
                                        token={personalLockToken}
                                        balance={personalLockBalance}
                                        onAmountChange={setPersonalLockAmount}
                                        amountIn={personalLockAmount}
                                        getTokenLogoUrl={getTokenLogoUrl}
                                    />
                                    <div style={{
                                        marginBottom: '20px',
                                        justifyContent: 'center', alignItems: 'center', display: 'flex', gap: '5px',
                                    }}>
                                        <Button
                                            label="Approve"
                                            onClick={() => handleApprove(personalLocker, new BigNumber(personalLockAmount), true)}
                                            disabled={!personalLockAmount || personalLockAmount === '0' || allowancePersonalLock.gte(new BigNumber(personalLockAmount))}
                                            style={{ marginBottom: '10px', width: '100%', borderRadius: '25px' }}
                                            size="small"
                                        />
                                        <Button
                                            label="Lock Tokens"
                                            onClick={handlePersonalLockTokens}
                                            disabled={!personalLockAmount || personalLockAmount === '0' || allowancePersonalLock.lt(new BigNumber(personalLockAmount))}
                                            style={{ marginBottom: '10px', width: '100%', borderRadius: '25px' }}
                                            size="small"
                                        />
                                    </div>
                                </InplaceContent>
                            </Inplace>

                            <div style={{
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                                borderRadius: '25px',
                                padding: '10px',
                            }}>



                                <div style={{ display: 'flex', alignContent: 'space-between', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginTop: '10px', flexWrap: 'wrap' }}>
                                    <div style={{
                                        fontSize: '14px', fontWeight: 'normal',
                                        //backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                        borderRadius: '15px',
                                        padding: '10px',
                                    }}>
                                        Total Value <br />
                                        <div style={{
                                            fontSize: '18px', fontWeight: 'bold',
                                        }}>
                                            {formatCurrencyExtended(
                                                personalLockBalances.reduce((acc, balance, index) => {
                                                    const token = tokenSelections?.find(t => t?.address.toLowerCase() === personalLockTokens[index]?.toLowerCase());
                                                    return acc + (parseFloat(balance) * parseFloat(token?.price) / (10 ** token?.decimals));
                                                }, 0))
                                            }</div>
                                    </div>
                                    {unclaimedRewards.length > 0 &&
                                        <div style={{
                                            fontSize: '14px', fontWeight: 'normal',
                                            //backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                            borderRadius: '15px',
                                            padding: '10px',
                                        }}>
                                            Claimable Rewards Value <br />
                                            <div style={{
                                                fontSize: '18px', fontWeight: 'bold',
                                            }}>
                                                {formatCurrencyExtended(
                                                    unclaimedRewards.reduce((acc, { token, reward }) => {
                                                        return acc + (parseFloat(reward) * parseFloat(token.price) / (10 ** token.decimals));
                                                    }, 0))
                                                }
                                            </div>
                                        </div>}

                                    <div style={{
                                        fontSize: '14px', fontWeight: 'normal',
                                        //backgroundColor: 'rgba(0, 0, 0, 0.05)',
                                        borderRadius: '15px',
                                        padding: '10px',
                                        overflow: 'auto',
                                    }}>
                                        Your Locker Address <br />
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            fontSize: '18px', fontWeight: 'bold',
                                        }}>
                                            {formatWalletAddress(personalLocker)}
                                            <Button icon="pi pi-copy" className="p-button-text" onClick={() => navigator.clipboard.writeText(personalLocker)} />
                                        </div>
                                    </div>
                                </div>

                                {/* {secondaryOwner && (
                                    <div style={{ marginBottom: '20px' }}>
                                        <h3>Set Secondary Owner</h3>
                                        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                            <input
                                                type="text"
                                                placeholder="Enter secondary owner address"
                                                value={newSecondaryOwner}
                                                onChange={(e) => setNewSecondaryOwner(e.target.value)}
                                                style={{
                                                    flex: 1,
                                                    borderRadius: '15px',
                                                    padding: '8px',
                                                    fontSize: '16px',
                                                }}
                                            />
                                            <Button label="Set" onClick={() => handleSetSecondaryOwner(newSecondaryOwner)} />
                                        </div>
                                    </div>
                                )}

                                {!isPrimaryOwnerLocked && (
                                    <div style={{ marginBottom: '20px' }}>
                                        <Button label="Lock Primary Owner Withdrawals" onClick={handleLockPrimaryOwnerWithdraws} />
                                    </div>
                                )} */}

                                {(personalLockTokens?.length > 0 || unclaimedRewards?.length > 0) && <>
                                    <div style={{ display: 'flex', alignContent: 'space-between', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                                        <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
                                            Your Personal Locker
                                        </div>
                                        <div>

                                            {unlockMode && <Button
                                                icon="pi pi-lock-open"
                                                label="Unlock All Tokens"
                                                onClick={handleUnlockAllTokens}
                                                rounded
                                                size="small"
                                                style={{ marginRight: '10px' }}
                                            />}
                                            {!unlockMode && <Button icon={radarView ? "pi pi-times" : "pi pi-wifi"} onClick={handleRadarView}
                                                rounded text
                                            />}
                                            <Button icon={unlockMode ? "pi pi-times" : "pi pi-cog"} onClick={handleToggleUnlockMode}
                                                rounded text
                                            />
                                        </div>
                                    </div>

                                    {personalLockTokens?.length > 0 ? personalLockTokens?.map((token, index) => {
                                        //find token inside tokenSelections and use that as the token object
                                        const _token = tokenSelections?.find(t => t?.address.toLowerCase() === personalLockTokens[index]?.toLowerCase());
                                        const balance = personalLockBalances[index];
                                        return (balance > 0 &&
                                            <NextCard key={token} style={miniTokenCardStyle(getTokenLogoUrl(_token))}>
                                                <NextCard.Body style={{ padding: '3px', overflow: 'auto' }}>
                                                    <div style={{ display: 'flex', alignContent: 'space-between', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>


                                                        <div>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold', fontSize: '18px' }}>
                                                                {_token && (
                                                                    <img src={getTokenLogoUrl(_token)} alt={_token?.label} style={{ width: '25px' }} />
                                                                )}
                                                                <span>{_token?.label}</span>

                                                            </div>
                                                            <div>
                                                                {formatDecimals(balance, _token?.decimals)}
                                                            </div>

                                                        </div>
                                                        {/* <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold', fontSize: '16px', flexWrap: 'wrap' }}>
                                                            <div style={{ flex: '1 1 auto' }}>{formatBalanceWithLogo(balance, _token, imageReferences)}</div>
                                                            <div style={{ flex: '0 1 auto' }}>{_token?.label}</div>
                                                        </div> */}
                                                        {/* <RewardsPending token={_token} tokenSelections={tokenSelections}  imageReference{imageReferences} /> */}

                                                        {radarView &&
                                                            <div>
                                                                <RewardsPending token={_token} account={personalLocker} contracts={contracts}  imageReferences={imageReferences} tokenSelections={tokenSelections}/>
                                                            </div>
                                                        }

                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap', textAlign: 'right' }}>
                                                            <div style={{ flex: '0 1 auto' }}>

                                                                {!unlockMode ?
                                                                    <div style={{ fontSize: '16px', fontWeight: 'normal' }}>
                                                                        {/* <div> <strong> {formatDecimals(balance, _token.decimals)}</strong></div> */}

                                                                        <div>{formatCurrencyExtended(parseFloat(balance) * parseFloat(_token?.price) / (10 ** _token?.decimals))}</div>
                                                                    </div>

                                                                    :
                                                                    <div>
                                                                        <Button icon="pi pi-lock-open" label="Unlock" rounded size='small' onClick={handlePersonalUnlockTokens.bind(this, _token, balance)} />
                                                                    </div>
                                                                }
                                                            </div>
                                                        </div>
                                                    </div>
                                                </NextCard.Body>
                                            </NextCard>
                                        );
                                    }) : <p>No tokens locked</p>}


                    

                                    {unclaimedRewards?.length > 0 && (
                                        <div>
                                            <h3>Claimable Rewards</h3>
                                            {unclaimedRewards.map(({ token, reward }) => (
                                                <NextCard key={token.address} style={miniTokenCardStyle(getTokenLogoUrl(token))}>
                                                    <NextCard.Body style={{ padding: '3px', overflow: 'auto' }}>
                                                        <div style={{ display: 'flex', alignContent: 'space-between', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontWeight: 'bold', fontSize: '16px', flexWrap: 'wrap' }}>
                                                                <div style={{ flex: '1 1 auto' }}>{formatBalanceWithLogo(reward, token, imageReferences)}</div>
                                                                <div style={{ flex: '0 1 auto' }}>{token.label}</div>
                                                            </div>
                                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap', textAlign: 'right' }}>
                                                                <div style={{ fontSize: '16px', fontWeight: 'normal', flex: '1 1 auto' }}>
                                                                    {formatCurrencyExtended(parseFloat(reward) * parseFloat(token.price) / (10 ** token.decimals))}
                                                                </div>
                                                                <div style={{ flex: '0 1 auto' }}>
                                                                    <Button icon="pi pi-wallet" label="Claim" rounded size='small' onClick={() => handleClaimRewardsToWallet(token)} />
                                                                    <Button icon="pi pi-lock" label="Claim" rounded size='small' onClick={() => handleClaimRewardsToLocker(token)} />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </NextCard.Body>
                                                </NextCard>
                                            ))}
                                        </div>
                                    )}
                                </>}

                            </div>
                        </div>
                    ) : (
                        <div style={{ textAlign: 'center', padding: '20px', backgroundColor: 'rgba(255, 255, 255, 0.1)', borderRadius: '15px', marginTop: '20px' }}>
                            <h2>Welcome to LockerBay!</h2>
                            <p>Safely unlock the full potential of your assets with a personal locker wallet. Store, secure, and manage your tokens & rewards all in one place.</p>
                            <Button label="Create Personal Locker" onClick={handleCreatePersonalLocker} rounded style={{ fontSize: '16px', padding: '10px 20px', marginTop: '20px' }} />
                        </div>
                    )}
                </TabPanel>
            </TabView>
            {showModal && (
                <TokenSelectorModal
                    tokens={filteredTokens}
                    onSelect={(token) => handleTokenSelect(token, showModal === 'timed' ? 'timed' : 'personal')}
                    onClose={handleCloseModal}
                    isVisible={showModal}
                    setShowModal={setShowModal}
                    getTokenLogoUrl={getTokenLogoUrl}
                    formatBalanceWithLogo={formatBalanceWithIcon}
                />
            )}
            <Toast ref={toast} />
        </div>
    );
});

export default LockerBay;
